<div class="public-request">
  @if (partnerPublicRequestState.loading()) {
  <section class="public-request__state public-request__state--loading">
    <ng-glyph name="refresh" size="22" class="public-request__spin"></ng-glyph>
    <p>Loading request form...</p>
  </section>
  } @else if (!partnerPublicRequestState.context()) {
  <section class="public-request__state">
    <ng-glyph name="link_off" size="34"></ng-glyph>
    <h1>Link unavailable</h1>
    <p>
      {{ partnerPublicRequestState.error() || "This request link has expired or is unavailable. Public links expire 12 hours after
      they are generated." }}
    </p>
  </section>
  } @else { @if (partnerPublicRequestState.context(); as context) {
  <header class="public-request__header">
    <div class="public-request__heading">
      <span class="public-request__eyebrow">Material Request</span>
      <h1>{{ context.partner.name }}</h1>
      <p>Submit a draft material request for admin review.</p>
    </div>
    <dl class="public-request__meta">
      <div>
        <dt>Warehouse</dt>
        <dd>{{ context.warehouse.name }}</dd>
      </div>
      <div>
        <dt>Step</dt>
        <dd>{{ step === 1 ? "Details" : step === 2 ? "Materials" : step === 3 ? "Review" : "Submitted" }}</dd>
      </div>
    </dl>
  </header>

  <nav class="public-request__steps" aria-label="Request steps">
    <span [class.public-request__step--active]="step === 1" [class.public-request__step--done]="step > 1">Details</span>
    <span [class.public-request__step--active]="step === 2" [class.public-request__step--done]="step > 2">Materials</span>
    <span [class.public-request__step--active]="step === 3" [class.public-request__step--done]="step > 3">Review</span>
  </nav>

  @if (step === 1) {
  <section class="public-request__surface">
    <div class="public-request__section-heading">
      <span>Step 1</span>
      <h2>Team leader details</h2>
      <p>These details create a new team leader assigned to {{ context.partner.name }}.</p>
    </div>

    <form [formGroup]="teamLeaderForm" class="public-request__form">
      <div class="public-request__field-grid">
        <label class="public-request__field">
          <span>Name</span>
          <input type="text" formControlName="name" autocomplete="name" />
        </label>
        <label class="public-request__field">
          <span>Email</span>
          <input type="email" formControlName="email" autocomplete="email" />
        </label>
        <label class="public-request__field">
          <span>Phone</span>
          <input type="tel" formControlName="phone" autocomplete="tel" />
        </label>
      </div>

      <div class="public-request__form-section">
        <div>
          <h3>Address</h3>
          <p>Select the administrative location and add street details.</p>
        </div>
        <app-location-selector (valueChange)="onAddressChange($event)"></app-location-selector>
      </div>

      <div class="public-request__field">
        <span>Note <small>Optional</small></span>
        <app-rich-text-editor formControlName="note" placeholder="Delivery notes or request context"></app-rich-text-editor>
      </div>
    </form>

    <footer class="public-request__actions">
      <app-button type="button" (click)="goToStep(2)">Continue</app-button>
    </footer>
  </section>
  } @if (step === 2) {
  <section class="public-request__surface public-request__surface--catalog">
    <div class="public-request__section-heading public-request__section-heading--inline">
      <div>
        <span>Step 2</span>
        <h2>Choose materials</h2>
        <p>Enter positive quantities for the materials needed.</p>
      </div>
      <div class="public-request__summary public-request__summary--top" aria-label="Current request summary">
        <strong>{{ selectedMaterialCount }}</strong>
        <span>materials</span>
        <strong>{{ selectedTotalQty }}</strong>
        <span>total qty</span>
      </div>
    </div>

    <div class="public-request__material-list">
      @for (material of context.materials; track material.id) {
      <article class="public-request__material-row" [class.public-request__material-row--selected]="getMaterialQty(material.id) > 0">
        <div class="public-request__material-image">
          @if (material.imageUrl) {
          <img [src]="material.imageUrl" [alt]="material.name" />
          } @else {
          <ng-glyph name="image" size="26"></ng-glyph>
          }
        </div>
        <div class="public-request__material-copy">
          <span>{{ material.sku }}</span>
          <h3>{{ material.name }}</h3>
          @if (getMaterialDescriptionPreview(material.id, material.description); as preview) {
          <p>{{ preview }}</p>
          }
        </div>
        <label class="public-request__qty">
          <span>Qty</span>
          <input
            appQtyInput
            type="number"
            min="0"
            step="1"
            [value]="getMaterialQty(material.id)"
            (input)="updateMaterialQty(material.id, $event)" />
        </label>
      </article>
      }
    </div>

    <footer class="public-request__summary-bar">
      <div>
        <strong>{{ selectedMaterialCount }}</strong>
        <span>selected materials</span>
      </div>
      <div>
        <strong>{{ selectedTotalQty }}</strong>
        <span>total quantity</span>
      </div>
      <div class="public-request__summary-actions">
        <app-button type="button" variant="secondary" (click)="goToStep(1)">Back</app-button>
        <app-button type="button" [disabled]="selectedMaterialCount === 0" (click)="goToStep(3)">Review Request</app-button>
      </div>
    </footer>
  </section>
  } @if (step === 3) {
  <section class="public-request__surface">
    <div class="public-request__section-heading public-request__section-heading--inline">
      <div>
        <span>Step 3</span>
        <h2>Review draft</h2>
        <p>Confirm the material quantities before submitting.</p>
      </div>
      <div class="public-request__summary public-request__summary--top" aria-label="Review summary">
        <strong>{{ selectedMaterialCount }}</strong>
        <span>materials</span>
        <strong>{{ selectedTotalQty }}</strong>
        <span>total qty</span>
      </div>
    </div>

    <div class="public-request__review">
      @for (item of selectedItems; track item.materialId) {
      <div class="public-request__review-row">
        <span>{{ getMaterialName(item.materialId) }}</span>
        <strong>{{ item.qty }}</strong>
      </div>
      }
    </div>

    <footer class="public-request__summary-bar public-request__summary-bar--review">
      <div>
        <strong>{{ selectedMaterialCount }}</strong>
        <span>selected materials</span>
      </div>
      <div>
        <strong>{{ selectedTotalQty }}</strong>
        <span>total quantity</span>
      </div>
      <div class="public-request__summary-actions">
        <app-button type="button" variant="secondary" (click)="goToStep(2)">Back</app-button>
        <app-button type="button" [disabled]="partnerPublicRequestState.submitting()" (click)="submit()">
          {{ partnerPublicRequestState.submitting() ? "Submitting..." : "Submit Draft Request" }}
        </app-button>
      </div>
    </footer>
  </section>
  } @if (step === 4 && partnerPublicRequestState.submitResult(); as submitResult) {
  <section class="public-request__state public-request__state--success">
    <ng-glyph name="check_circle" size="40"></ng-glyph>
    <h2>Draft submitted</h2>
    <p>Your request number is <strong>{{ submitResult.requestNo }}</strong>.</p>
    <p>The team will review this draft before fulfillment.</p>
  </section>
  } } }
</div>
